// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CompareArrowsRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography } from '@mui/material';

interface MergeResultLike<Item> {
  merged: Item;
  retained: Item;
  removed: Item;
}

interface Props<Item extends object> {
  /** Heading above the field rows, e.g. "Merged customer:" */
  title: string;
  result: MergeResultLike<Item>;
  keyLabels: Record<keyof Item & string, string>;
  /** Keys rendered first, in the given order, ahead of the sorted remainder */
  pinnedKeys: readonly (keyof Item & string)[];
  ignoredKeys: readonly (keyof Item & string)[];
  /** Copy shown for a missing field value, e.g. "None" */
  emptyValueLabel: string;
  /**
   * Optional per-key display mapping, for values stored as an enum and rendered as a label.
   * Returning undefined falls through to the default rendering.
   */
  getValueLabel?: (
    key: keyof Item & string,
    value: unknown
  ) => string | null | undefined;
}

/**
 * Converts a merge property value to a display string.
 * Booleans are converted to "Yes"/"No"; sparse entity objects render by name.
 */
function valueToString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    if ('label' in value && typeof value.label === 'string') {
      return value.label;
    }
    if ('name' in value) {
      return typeof value.name === 'string' ? value.name : null;
    }
    return JSON.stringify(value);
  }
  return null;
}

/**
 * Renders the field-by-field before/after breakdown of an entity merge audit
 * property: removed value → retained value per field, underlining whichever
 * side persisted onto the merged record.
 */
export function AuditMergedEntityLabel<Item extends object>(
  props: Props<Item>
) {
  const isRenderableKey = (key: string): key is keyof Item & string =>
    key in props.keyLabels &&
    !props.pinnedKeys.includes(key as keyof Item & string) &&
    !props.ignoredKeys.includes(key as keyof Item & string);

  const mergedKeys: (keyof Item & string)[] = [
    ...props.pinnedKeys,
    ...Object.keys(props.result.merged ?? {})
      .filter(isRenderableKey)
      .sort((a, b) => props.keyLabels[a].localeCompare(props.keyLabels[b])),
  ];

  return (
    <Stack spacing={0.5}>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.title}
      </Typography>
      {mergedKeys.map((key) => {
        const displayValue = (value: unknown) => {
          const label = props.getValueLabel?.(key, value);
          return label === undefined ? valueToString(value) : label;
        };

        const retainedValue = displayValue(props.result.retained[key]);
        const mergedValue = displayValue(props.result.merged[key]);
        const removedValue = displayValue(props.result.removed[key]);

        // Persisted tracks which value "persisted" during the merge, if both the retained and removed are equal, this is null
        const persisted =
          retainedValue === removedValue ? null : retainedValue === mergedValue;

        return (
          <Typography
            key={key}
            variant="caption"
            paddingLeft={2}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              component="span"
              variant="inherit"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {props.keyLabels[key]}:
            </Typography>
            <Typography
              component="span"
              variant="inherit"
              sx={{
                textDecoration: persisted === false ? 'underline' : 'none',
                ml: 0.5,
              }}
            >
              {removedValue ?? (
                <Typography
                  component="span"
                  variant="inherit"
                  color="text.secondary"
                >
                  {props.emptyValueLabel}
                </Typography>
              )}
            </Typography>
            <CompareArrowsRounded fontSize="inherit" sx={{ marginX: 1 }} />
            <Typography
              component="span"
              variant="inherit"
              sx={{
                textDecoration: persisted === true ? 'underline' : 'none',
              }}
            >
              {retainedValue ?? (
                <Typography
                  component="span"
                  variant="inherit"
                  color="text.secondary"
                >
                  {props.emptyValueLabel}
                </Typography>
              )}
            </Typography>
          </Typography>
        );
      })}
    </Stack>
  );
}
